import { createFileRoute, Outlet, redirect, useMatchRoute } from '@tanstack/react-router';
import { Check } from 'lucide-react';

export const Route = createFileRoute('/app/onboarding')({
  component: OnboardingLayout,
});

const steps = [
  { number: 1, label: 'Profile', path: '/app/onboarding/step-1' },
  { number: 2, label: 'Accounts', path: '/app/onboarding/step-2' },
  { number: 3, label: 'Community', path: '/app/onboarding/step-3' },
] as const;

function OnboardingLayout() {
  const matchRoute = useMatchRoute();

  const currentStep =
    steps.findIndex((s) => !!matchRoute({ to: s.path })) + 1 || 1;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface-950 overflow-y-auto">
      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.08)_0%,_transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(38,217,181,0.04)_0%,_transparent_40%)]" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute inset-0 noise-overlay" />
      </div>

      {/* Top bar with logo */}
      <div className="relative shrink-0 flex items-center justify-center pt-8 pb-6">
        <img src="/logos/logo_stacked.svg" alt="Rivaled" className="h-20" />
      </div>

      {/* Step indicator */}
      <div className="relative shrink-0 grid grid-cols-3 w-full max-w-lg mx-auto pb-8 px-8">
        {steps.map((step, i) => {
          const isActive = step.number === currentStep;
          const isComplete = step.number < currentStep;

          return (
            <div key={step.number} className="flex flex-col items-center gap-1.5 relative">
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div
                  className={`absolute top-4 left-[calc(50%+18px)] right-[calc(-50%+18px)] h-px transition-colors duration-300 ${
                    step.number < currentStep ? 'bg-brand-500/50' : 'bg-surface-700/50'
                  }`}
                />
              )}
              <div
                className={`
                  relative z-10 flex items-center justify-center size-9 rounded-full font-display font-bold text-sm transition-all duration-300
                  ${isComplete
                    ? 'bg-brand-500 text-surface-900 shadow-[0_0_16px_rgba(38,217,181,0.4)]'
                    : isActive
                      ? 'bg-brand-500/15 text-brand-400 border-2 border-brand-500 shadow-[0_0_20px_rgba(38,217,181,0.25)]'
                      : 'bg-surface-800 text-surface-500 border border-surface-700'
                  }
                `}
              >
                {isComplete ? <Check className="size-4" /> : step.number}
              </div>
              <span
                className={`text-[10px] uppercase tracking-widest font-semibold transition-colors duration-300 ${
                  isActive ? 'text-brand-400' : isComplete ? 'text-brand-500/60' : 'text-surface-600'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Content area */}
      <div className="relative flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-lg">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
